// ServiceLogViewModal types and component

import React from 'react';

interface StatusEntry {
  label: string;
  color: string;
  bg: string;
  icon: string;
}

// Status configuration for service logs
const STATUS_CONFIG: Record<string, StatusEntry> = {
  pending: { label: 'Pending', color: 'var(--color-warning)', bg: 'var(--color-warning-bg)', icon: '⏳' },
  draft: { label: 'Draft', color: 'var(--color-text-light)', bg: 'var(--color-bg)', icon: '📝' },
  sent: { label: 'Sent', color: 'var(--color-info)', bg: 'var(--color-info-bg)', icon: '📤' },
  overdue: { label: 'Overdue', color: 'var(--color-error)', bg: 'var(--color-error-bg)', icon: '⚠️' },
  paid: { label: 'Paid', color: 'var(--color-success)', bg: 'var(--color-success-bg)', icon: '✓' },
  void: { label: 'Void', color: 'var(--color-text-light)', bg: 'var(--color-bg)', icon: '⊘' }
};

interface PaymentMethodEntry {
  label: string;
  color: string;
  bg: string;
}

const PAYMENT_METHOD_CONFIG: Record<string, PaymentMethodEntry> = {
  paystack: { label: '💳 Paystack', color: '#0066cc', bg: '#e6f3ff' },
  eft: { label: '🏦 EFT', color: 'var(--color-success-text)', bg: 'var(--color-success-bg)' },
  cash: { label: '💵 Cash', color: '#ca8a04', bg: '#fef9c3' }
};

interface ServiceLogItem {
  item_name: string;
  quantity: number;
  unit_price?: number | string;
  line_total?: number;
  display_order?: number;
  section_label?: string | null;
}

interface ServiceLogInvoice {
  status?: string;
  subtotal?: number;
  vat_amount?: number;
  invoice_number?: string;
}

interface ServiceLogSplitCode {
  code: string;
  description?: string;
}

interface ServiceLog {
  status: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  service_date?: string;
  service_location?: string;
  booking_code?: string;
  territory_name?: string;
  split_code?: ServiceLogSplitCode;
  items?: ServiceLogItem[];
  vat_inclusive?: boolean;
  total_amount?: number;
  discount_amount?: number;
  referral_discount_applied?: number;
  voucher_discount_applied?: number;
  invoice_number?: string;
  invoice?: ServiceLogInvoice;
  payment_method?: string;
  notes?: string;
  internal_notes?: string;
}

interface ServiceLogViewModalProps {
  log: ServiceLog | null;
  onClose: () => void;
  onEdit?: (log: ServiceLog) => void;
  onApprove?: (log: ServiceLog) => void;
  onSend?: (log: ServiceLog) => void;
  canEdit?: boolean;
  canApprove?: boolean;
  canSend?: boolean;
  formatCurrency: (amount: number) => string;
  formatDate: (dateStr: string | null | undefined) => string;
}

/**
 * ServiceLogViewModal - Reusable modal for viewing service log details
 * Used by: UserHome dashboard, ServiceLogs page
 */
function ServiceLogViewModal({
  log,
  onClose,
  onEdit,
  onApprove,
  onSend,
  canEdit = false,
  canApprove = false,
  canSend = false,
  formatCurrency,
  formatDate
}: ServiceLogViewModalProps) {
  if (!log) return null;

  const status = STATUS_CONFIG[log.status] ?? STATUS_CONFIG['pending']!;
  const canEditThisLog = canEdit && log.status === 'pending';
  const canApproveThisLog = canApprove && log.status === 'pending';
  // Must have send permission AND invoice must be in draft status
  const canSendThisLog = canSend && log.invoice?.status?.toLowerCase() === 'draft';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        {/* Header */}
        <div className="modal-header">
          <div className="flex items-center gap-md">
            <h2 className="m-0">Service Log</h2>
            <span
              className="status-badge"
              style={{ background: status.bg, color: status.color }}
            >
              {status.icon} {status.label}
            </span>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Customer Section */}
          <div className="view-section">
            <div className="section-title">Customer</div>
            <div style={{ fontWeight: 600, fontSize: 'var(--text-2xl)' }}>{log.customer_name || '-'}</div>
            {log.customer_email && (
              <div className="text-sm text-muted">
                {log.customer_email}
              </div>
            )}
            {log.customer_phone && (
              <div className="text-sm text-muted">
                {log.customer_phone}
              </div>
            )}
          </div>

          {/* Service Details */}
          <div className="view-section">
            <div className="section-title">Service Details</div>
            <div className="view-row">
              <span className="view-label">Service Date</span>
              <span>{formatDate(log.service_date)}</span>
            </div>
            {log.service_location && (
              <div className="view-row">
                <span className="view-label">Location</span>
                <span style={{ textAlign: 'right', maxWidth: '60%' }}>{log.service_location}</span>
              </div>
            )}
            {log.booking_code && (
              <div className="view-row">
                <span className="view-label">Booking Code</span>
                <span className="code-chip">{log.booking_code}</span>
              </div>
            )}
            {log.territory_name && (
              <div className="view-row">
                <span className="view-label">Territory</span>
                <span className="code-chip">{log.territory_name}</span>
              </div>
            )}
            {log.split_code?.code && (
              <div className="view-row">
                <span className="view-label">Split Code</span>
                <span className="code-chip">{log.split_code.description || log.split_code.code}</span>
              </div>
            )}
          </div>

          {/* Line Items — grouped by section when section_label is set
              (hospitality / POS logs). For CMB logs items render as
              one flat table as before. */}
          {log.items && log.items.length > 0 && (() => {
            const sortedItems = [...log.items].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
            const hasSections = sortedItems.some(i => i.section_label && i.section_label.trim());
            if (!hasSections) {
              return (
                <div className="view-section">
                  <div className="section-title">Service Items ({sortedItems.length})</div>
                  <table className="modal-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th style={{ textAlign: 'center', width: '50px' }}>Qty</th>
                        <th className="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedItems.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.item_name}</td>
                          <td className="text-center">{item.quantity}</td>
                          <td className="text-right fw-500">
                            {formatCurrency(item.line_total || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            }
            // Group preserving display_order. Items without a section
            // fall under an "Unassigned" group so nothing goes missing.
            const sectionsInOrder: string[] = [];
            const grouped = new Map<string, ServiceLogItem[]>();
            sortedItems.forEach(it => {
              const lbl = (it.section_label && it.section_label.trim()) || 'Unassigned';
              if (!grouped.has(lbl)) { grouped.set(lbl, []); sectionsInOrder.push(lbl); }
              grouped.get(lbl)!.push(it);
            });
            const totalCount = sortedItems.length;
            return (
              <div className="view-section">
                <div className="section-title">Rooms / Sections ({totalCount} item{totalCount === 1 ? '' : 's'})</div>
                <table className="modal-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th style={{ textAlign: 'center', width: '50px' }}>Qty</th>
                      <th className="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectionsInOrder.map(section => {
                      const sectionItems = grouped.get(section)!;
                      const sectionSubtotal = sectionItems.reduce((sum, it) => sum + (it.line_total || 0), 0);
                      return (
                        <React.Fragment key={section}>
                          <tr style={{ background: 'var(--color-bg)' }}>
                            <td colSpan={2} style={{ fontWeight: 600, color: 'var(--color-sidebar)' }}>{section}</td>
                            <td className="text-right font-bold text-primary">
                              {formatCurrency(sectionSubtotal)}
                            </td>
                          </tr>
                          {sectionItems.map((item, idx) => (
                            <tr key={`${section}-${idx}`}>
                              <td style={{ paddingLeft: '1.25rem' }}>{item.item_name}</td>
                              <td className="text-center">{item.quantity}</td>
                              <td className="text-right fw-500">
                                {formatCurrency(item.line_total || 0)}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Totals */}
          <div className="view-section">
            <div className="section-title">
              Totals {log.vat_inclusive ? '(VAT Inclusive)' : '(VAT Exclusive)'}
            </div>

            {/* Subtotal - sum of line items */}
            <div className="view-row">
              <span className="view-label">Subtotal</span>
              <span>
                {formatCurrency(
                  log.items?.reduce((sum, item) => sum + (item.line_total || 0), 0) ||
                  log.invoice?.subtotal ||
                  log.total_amount || 0
                )}
              </span>
            </div>

            {/* Service discount. Was missing entirely, so a log with both this and a
                voucher showed a Subtotal and Total that did not reconcile. */}
            {(log.discount_amount ?? 0) > 0 && (
              <div className="view-row">
                <span className="view-label text-success">Discount</span>
                <span className="text-success">
                  -{formatCurrency(log.discount_amount ?? 0)}
                </span>
              </div>
            )}

            {/* Referral Discount */}
            {(log.referral_discount_applied ?? 0) > 0 && (
              <div className="view-row">
                <span className="view-label text-success">Referral Discount</span>
                <span className="text-success">
                  -{formatCurrency(log.referral_discount_applied ?? 0)}
                </span>
              </div>
            )}

            {/* Voucher Discount */}
            {(log.voucher_discount_applied ?? 0) > 0 && (
              <div className="view-row">
                <span className="view-label text-success">Voucher Discount</span>
                <span className="text-success">
                  -{formatCurrency(log.voucher_discount_applied ?? 0)}
                </span>
              </div>
            )}

            {/* VAT Amount - show if invoice has vat_amount */}
            {(log.invoice?.vat_amount ?? 0) > 0 && (
              <div className="view-row">
                <span className="view-label">VAT (15%)</span>
                <span className="text-muted">
                  {formatCurrency(log.invoice?.vat_amount ?? 0)}
                </span>
              </div>
            )}

            {/* Total */}
            <div
              className="view-row"
              style={{
                fontWeight: 600,
                fontSize: 'var(--text-2xl)',
                borderTop: '1px solid var(--color-border)',
                paddingTop: '0.75rem',
                marginTop: '0.5rem'
              }}
            >
              <span>Total</span>
              <span className="text-primary">
                {formatCurrency(log.total_amount || 0)}
              </span>
            </div>
          </div>

          {/* Invoice Info */}
          {(log.invoice_number || log.invoice?.invoice_number) && (
            <div className="view-section">
              <div className="section-title">Invoice</div>
              <div className="view-row">
                <span className="view-label">Invoice #</span>
                <span className="code-chip" style={{ fontFamily: 'monospace' }}>
                  {log.invoice_number || log.invoice?.invoice_number}
                </span>
              </div>
              {log.status === 'paid' && log.payment_method && (
                <div className="view-row">
                  <span className="view-label">Payment</span>
                  <span
                    className="status-badge"
                    style={{
                      background: PAYMENT_METHOD_CONFIG[log.payment_method]?.bg || 'var(--color-bg)',
                      color: PAYMENT_METHOD_CONFIG[log.payment_method]?.color || 'var(--color-sidebar-hover)'
                    }}
                  >
                    {PAYMENT_METHOD_CONFIG[log.payment_method]?.label || log.payment_method}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div className="view-section" style={{ borderBottom: 'none' }}>
            <div className="section-title">Notes</div>
            <div className="mb-md">
              <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-primary)', marginBottom: '0.25rem' }}>Customer-facing (appears on invoice PDF)</div>
              <div style={{ background: 'var(--color-bg-light)', padding: '0.75rem', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-base)', whiteSpace: 'pre-wrap', color: log.notes ? undefined : 'var(--color-text-light)' }}>{log.notes || 'None'}</div>
            </div>
            <div>
              <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-light)', marginBottom: '0.25rem' }}>Internal only (not visible to customer)</div>
              <div style={{ background: 'var(--color-bg-light)', padding: '0.75rem', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-base)', whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>{log.internal_notes || 'None'}</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {canEditThisLog && onEdit && (
            <button className="btn btn-outline" onClick={() => onEdit(log)}>
              Edit
            </button>
          )}
          {canSendThisLog && onSend && (
            <button className="btn btn-primary" onClick={() => onSend(log)}>
              Send Invoice
            </button>
          )}
          {canApproveThisLog && onApprove && (
            <button className="btn btn-primary" onClick={() => onApprove(log)}>
              Approve
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ServiceLogViewModal;
